import type { PointerEvent as ReactPointerEvent } from 'react'
import type { Table } from '@/types'
import { useDiagramStore, useHistoryStore, useSelectionStore, useViewportStore } from '@/stores'
import { useTabViewStore } from '@/stores/tabViewStore'
import { clientToCanvas } from '@/utils/geometry'
import { snapToTableBorder } from '@/utils/paths'

function commitDiagramHistory(label: string) {
  const { tables, rels, groups, defaults, projectName, dialect } = useDiagramStore.getState()
  const hiddenTableIds = useTabViewStore.getState().hiddenTableIds
  useHistoryStore.getState().commitHistory({ tables, rels, groups, defaults, projectName, dialect, hiddenTableIds }, label)
}

export function useAnchorDrag() {
  return (
    event: ReactPointerEvent<SVGCircleElement>,
    relationId: string,
    side: 'from' | 'to',
    table: Pick<Table, 'x' | 'y' | 'cols'>,
  ) => {
    event.preventDefault()
    event.stopPropagation()
    useSelectionStore.getState().selectRelationship(relationId)

    const relation = useDiagramStore.getState().rels.find((item) => item.id === relationId)
    if (!relation) {
      return
    }

    const originalAnchor = side === 'from' ? relation.fromAnchor : relation.toAnchor
    let changed = false
    let isFinished = false

    const updateAnchor = (clientX: number, clientY: number) => {
      const canvas = document.getElementById('cwrap')
      if (!canvas) return

      const rect = canvas.getBoundingClientRect()
      const { sc, tx, ty } = useViewportStore.getState()
      const point = clientToCanvas(clientX, clientY, rect, { sc, tx, ty })
      const anchor = snapToTableBorder(point.x, point.y, table)

      useDiagramStore.setState((state) => ({
        rels: state.rels.map((item) =>
          item.id === relationId
            ? { ...item, [side === 'from' ? 'fromAnchor' : 'toAnchor']: anchor }
            : item,
        ),
      }))
      changed = true
    }

    const onPointerMove = (moveEvent: PointerEvent) => {
      updateAnchor(moveEvent.clientX, moveEvent.clientY)
    }

    const cleanup = () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('keydown', onKeyDown)
      isFinished = true
    }

    const onPointerUp = () => {
      cleanup()
      if (changed) commitDiagramHistory(`move-anchor:${relationId}:${side}`)
    }

    const onKeyDown = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key !== 'Escape' || isFinished) return
      cleanup()
      useDiagramStore.setState((state) => ({
        rels: state.rels.map((item) =>
          item.id === relationId
            ? { ...item, [side === 'from' ? 'fromAnchor' : 'toAnchor']: originalAnchor }
            : item,
        ),
      }))
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp, { once: true })
    window.addEventListener('keydown', onKeyDown)

    const target = event.currentTarget
    target.setPointerCapture?.(event.pointerId)
  }
}
